/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsDismissSuggestion } from "../funcs/skillsDismissSuggestion.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SkillEditSuggestion } from "../models/components/skilleditsuggestion.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DismissSkillSuggestionRequest,
  DismissSkillSuggestionSecurity,
} from "../models/operations/dismissskillsuggestion.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DismissSkillSuggestionMutationVariables = {
  request: DismissSkillSuggestionRequest;
  security?: DismissSkillSuggestionSecurity | undefined;
  options?: RequestOptions;
};

export type DismissSkillSuggestionMutationData = SkillEditSuggestion;

export type DismissSkillSuggestionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * dismissSuggestion skills
 *
 * @remarks
 * Idempotently dismiss an open skill edit suggestion. Approved and superseded suggestions conflict.
 */
export function useDismissSkillSuggestionMutation(
  options?: MutationHookOptions<
    DismissSkillSuggestionMutationData,
    DismissSkillSuggestionMutationError,
    DismissSkillSuggestionMutationVariables
  >,
): UseMutationResult<
  DismissSkillSuggestionMutationData,
  DismissSkillSuggestionMutationError,
  DismissSkillSuggestionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDismissSkillSuggestionMutation(client, options),
    ...options,
  });
}

export function mutationKeyDismissSkillSuggestion(): MutationKey {
  return ["@gram/client", "skills", "dismissSuggestion"];
}

export function buildDismissSkillSuggestionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DismissSkillSuggestionMutationVariables,
  ) => Promise<DismissSkillSuggestionMutationData>;
} {
  return {
    mutationKey: mutationKeyDismissSkillSuggestion(),
    mutationFn: function dismissSkillSuggestionMutationFn({
      request,
      security,
      options,
    }): Promise<DismissSkillSuggestionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsDismissSuggestion(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
